import React, { useState } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { PaymentData } from '../types';
import { Investment } from '../../../types';
import { CreditCard, DollarSign, Calendar, TrendingUp, AlertCircle, Edit, Save, X, Clock } from 'lucide-react';
import { useCalculator } from '../../../context/CalculatorContext';

interface AmortizationTabProps {
  paymentData: PaymentData;
  investment: Investment;
}

const AmortizationTab: React.FC<AmortizationTabProps> = ({ paymentData, investment }) => {
  const { updateInvestment } = useCalculator();
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Array<{
    paymentNumber: number;
    date: string;
    paymentAmount: number;
    principalAmount: number;
    interestAmount: number;
    remainingBalance: number;
    cumulativePrincipal: number;
    cumulativeInterest: number;
  }>>([]);
  const [downPaymentDate, setDownPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonthlyDay, setSelectedMonthlyDay] = useState(15); // Default to 15th of each month

  const totalInvestment = investment.initialCertificates * investment.certificateBasePrice;
  const downPaymentAmount = totalInvestment * (investment.downPaymentPercentage || 30) / 100;
  const totalPaid = downPaymentAmount + (paymentData.totalFinanced);
  const totalWithInterest = downPaymentAmount + paymentData.amortizationTable.reduce((sum, entry) => sum + entry.paymentAmount, 0);

  // Initialize editing schedule
  React.useEffect(() => {
    if (paymentData.amortizationTable) {
      setEditingSchedule([...paymentData.amortizationTable]);
    }
  }, [paymentData.amortizationTable]);

  const handleStartEditing = () => {
    setIsEditingDates(true);
    setEditingSchedule([...paymentData.amortizationTable]);
  };

  const handleCancelEditing = () => {
    setIsEditingDates(false);
    setEditingSchedule([...paymentData.amortizationTable]);
  };

  const handleSaveSchedule = () => {
    setIsEditingDates(false);
    alert('Fechas de pago guardadas correctamente');
  };

  const handleDateChange = (paymentNumber: number, newDate: string) => {
    setEditingSchedule(prev => 
      prev.map(entry => 
        entry.paymentNumber === paymentNumber 
          ? { ...entry, date: new Date(newDate).toLocaleDateString('es-MX', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            }) }
          : entry
      )
    );
  };

  // Apply selected monthly day to all payments
  const handleApplyMonthlyDay = () => {
    const startDate = new Date(downPaymentDate);
    startDate.setMonth(startDate.getMonth() + 1); // Start next month after down payment
    
    const newSchedule = editingSchedule.map((entry, index) => {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(startDate.getMonth() + index);
      paymentDate.setDate(selectedMonthlyDay);
      
      return {
        ...entry,
        date: paymentDate.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      };
    });
    
    setEditingSchedule(newSchedule);
  };
  const generateCustomDates = (pattern: 'monthly' | 'bimonthly' | 'quarterly') => {
    const startDate = new Date(downPaymentDate);
    startDate.setMonth(startDate.getMonth() + 1); // Start next month after down payment
    startDate.setDate(selectedMonthlyDay); // Use selected day
    
    let increment: number;
    switch (pattern) {
      case 'monthly':
        increment = 1;
        break;
      case 'bimonthly':
        increment = 2;
        break;
      case 'quarterly':
        increment = 3;
        break;
      default:
        increment = 1;
    }

    const newSchedule = editingSchedule.map((entry, index) => {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(startDate.getMonth() + (index * increment));
      paymentDate.setDate(selectedMonthlyDay); // Ensure consistent day
      
      return {
        ...entry,
        date: paymentDate.toLocaleDateString('es-MX')
      };
    });

    setEditingSchedule(newSchedule);
  };

  return (
    <div className="space-y-8">
      {/* Amortization Summary */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Resumen de Financiamiento</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="text-sm text-purple-600 mb-1">Monto Financiado</div>
            <div className="text-xl font-bold text-purple-700">
              {formatCurrency(paymentData.totalFinanced, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
            <div className="text-sm text-blue-600 mb-1">Mensualidad</div>
            <div className="text-xl font-bold text-blue-700">
              {formatCurrency(paymentData.totalFinanced / 48, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
            <div className="text-sm text-green-600 mb-1">Plazo</div>
            <div className="text-xl font-bold text-green-700">48</div>
            <div className="text-xs text-green-600">meses</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
            <div className="text-sm text-orange-600 mb-1">Interés Total</div>
            <div className="text-xl font-bold text-orange-700">
              {formatCurrency(paymentData.totalInterest, investment.currencyFormat)}
            </div>
          </div>
        </div>
      </div>

      {/* Date Editing Controls */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Personalización de Fechas de Pago</h3>
          </div>
          
          <div className="flex items-center gap-2">
            {isEditingDates ? (
              <>
                <button
                  onClick={handleCancelEditing}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveSchedule}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Guardar Fechas
                </button>
              </>
            ) : (
              <button
                onClick={handleStartEditing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Editar Fechas
              </button>
            )}
          </div>
        </div>

        {/* Quick Date Patterns (only when editing) */}
        {isEditingDates && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-3">Patrones de Fechas Rápidos</h4>
            
            {/* Default Monthly Day Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Día del Mes para Todos los Pagos Mensuales
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={selectedMonthlyDay}
                  onChange={(e) => setSelectedMonthlyDay(parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>
                      Día {day}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleApplyMonthlyDay}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Aplicar a Todos
                </button>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Selecciona el día del mes que se repetirá para todos los pagos mensuales
              </p>
            </div>
            
            {/* Quick Day Presets */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Días Comunes (Aplicación Inmediata)
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 5, 15, 30].map(day => (
                  <button
                    key={day}
                    onClick={() => {
                      setSelectedMonthlyDay(day);
                      // Auto-apply when clicking preset
                      const startDate = new Date(downPaymentDate);
                      startDate.setMonth(startDate.getMonth() + 1); // Start next month after down payment
                      
                      const newSchedule = editingSchedule.map((entry, index) => {
                        const paymentDate = new Date(startDate);
                        paymentDate.setMonth(startDate.getMonth() + index);
                        paymentDate.setDate(day);
                        
                        return {
                          ...entry,
                          date: paymentDate.toLocaleDateString('es-MX')
                        };
                      });
                      
                      setEditingSchedule(newSchedule);
                    }}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      selectedMonthlyDay === day
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Haz clic en cualquier día para aplicarlo inmediatamente a todos los pagos
              </p>
            </div>
            
            {/* Down Payment Date */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Fecha del Enganche
              </label>
              <input
                type="date"
                value={downPaymentDate}
                onChange={(e) => setDownPaymentDate(e.target.value)}
                className="px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Pattern Generation Buttons */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Patrones de Fechas Automáticos
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  onClick={() => generateCustomDates('monthly')}
                  className="p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">Mensual</span>
                  </div>
                  <p className="text-sm text-blue-600">Pagos cada mes en día {selectedMonthlyDay}</p>
                </button>
                
                <button
                  onClick={() => generateCustomDates('bimonthly')}
                  className="p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">Bimensual</span>
                  </div>
                  <p className="text-sm text-blue-600">Pagos cada 2 meses en día {selectedMonthlyDay}</p>
                </button>
                
                <button
                  onClick={() => generateCustomDates('quarterly')}
                  className="p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">Trimestral</span>
                  </div>
                  <p className="text-sm text-blue-600">Pagos cada 3 meses en día {selectedMonthlyDay}</p>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Standard Down Payment Display */}
      {!investment.enableCustomDownPaymentSchedule && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Enganche</h3>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-700 mb-2">
                {formatCurrency(downPaymentAmount, investment.currencyFormat)}
              </div>
              <div className="text-green-600">
                {investment.downPaymentPercentage || 30}% del valor total
              </div>
              <div className="text-sm text-green-600 mt-2">
                Pago único al inicio del contrato
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Amortization Table with Down Payment */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Tabla de Amortización Completa (Enganche + 48 Mensualidades)</h3>
        </div>
        
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pago #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pago Total
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capital
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interés
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Saldo
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capital Acum.
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interés Acum.
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Down Payment Row */}
              <tr className="bg-green-50 font-medium">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-700">
                  0
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700">
                  {isEditingDates ? (
                    <input
                      type="date"
                      value={downPaymentDate}
                      onChange={(e) => setDownPaymentDate(e.target.value)}
                      className="px-2 py-1 border border-green-300 rounded focus:ring-2 focus:ring-green-500 text-sm"
                    />
                  ) : (
                    new Date(downPaymentDate).toLocaleDateString('es-MX')
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-700">
                  ENGANCHE
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                  {formatCurrency(downPaymentAmount, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                  {formatCurrency(downPaymentAmount, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-700">
                  {formatCurrency(0, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                  {formatCurrency(paymentData.totalFinanced, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                  {formatCurrency(downPaymentAmount, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-700">
                  {formatCurrency(0, investment.currencyFormat)}
                </td>
              </tr>
              
              {/* Monthly Payments */}
              {(isEditingDates ? editingSchedule : paymentData.amortizationTable).map((entry, index) => (
                <tr key={index} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.paymentNumber}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {isEditingDates ? (
                      <input
                        type="date"
                        value={new Date(entry.date.split('/').reverse().join('-')).toISOString().split('T')[0]}
                        onChange={(e) => handleDateChange(entry.paymentNumber, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm w-full"
                      />
                    ) : (
                      entry.date
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    Mensualidad
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(entry.paymentAmount, investment.currencyFormat)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                    {formatCurrency(entry.principalAmount, investment.currencyFormat)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600">
                    {formatCurrency(entry.interestAmount, investment.currencyFormat)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(entry.remainingBalance, investment.currencyFormat)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                    {formatCurrency(downPaymentAmount + entry.cumulativePrincipal, investment.currencyFormat)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600">
                    {formatCurrency(entry.cumulativeInterest, investment.currencyFormat)}
                  </td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr className="bg-blue-50 font-bold border-t-2 border-blue-200">
                <td colSpan={3} className="px-4 py-3 text-sm font-bold text-blue-700">
                  TOTALES FINALES
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-700">
                  {formatCurrency(totalWithInterest, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-700">
                  {formatCurrency(totalInvestment, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-700">
                  {formatCurrency(paymentData.totalInterest, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-700">
                  {formatCurrency(0, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-700">
                  {formatCurrency(totalInvestment, investment.currencyFormat)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-700">
                  {formatCurrency(paymentData.totalInterest, investment.currencyFormat)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Payment Summary */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-blue-800">Resumen de Pagos</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-600">Total de Pagos:</span>
              <div className="font-bold text-blue-700">{48 + 1} pagos</div>
            </div>
            <div>
              <span className="text-blue-600">Enganche:</span>
              <div className="font-bold text-green-700">{formatCurrency(downPaymentAmount, investment.currencyFormat)}</div>
            </div>
            <div>
              <span className="text-blue-600">Mensualidades:</span>
              <div className="font-bold text-purple-700">48 × {formatCurrency(paymentData.totalFinanced / 48, investment.currencyFormat)}</div>
            </div>
            <div>
              <span className="text-blue-600">Total Pagado:</span>
              <div className="font-bold text-blue-700">{formatCurrency(totalWithInterest, investment.currencyFormat)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Instructions */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Instrucciones de Pago</h3>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Pagos Mensuales</h4>
            <p className="text-sm text-blue-700">
              Los pagos mensuales se realizan según el cronograma personalizado. 
              {isEditingDates ? ' Puedes modificar las fechas según tus necesidades.' : ' Usa el botón "Editar Fechas" para personalizar el cronograma.'}
            </p>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Métodos de Pago Aceptados</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Transferencia bancaria</li>
              <li>• Depósito en cuenta</li>
              <li>• Pago con tarjeta de crédito/débito</li>
              <li>• Domiciliación bancaria (recomendado)</li>
            </ul>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Datos Bancarios</h4>
            <div className="text-sm text-green-700 space-y-3">
              <div>
                <div className="font-bold text-green-800 mb-1">FIDECOMISO 697 - SOLO TRANSFERENCIA</div>
                <div><strong>Banco:</strong> INSTITUCIÓN DE BANCA MÚLTIPLE, GRUPO FINANCIERO VE POR MÁS</div>
                <div><strong>No. de Cuenta:</strong> 0000070758</div>
                <div><strong>CLABE:</strong> 113180000007017586</div>
              </div>
              <div>
                <div className="font-bold text-green-800 mb-1">BBVA MÉXICO - TRANSFERENCIA Y EFECTIVO</div>
                <div><strong>No. de Cuenta:</strong> 0118891737</div>
                <div><strong>CLABE:</strong> 012910001188917370</div>
              </div>
              <div>
                <div className="font-bold text-green-800 mb-1">SANTANDER - TRANSFERENCIA Y EFECTIVO</div>
                <div><strong>No. de Cuenta:</strong> 65 50166596</div>
                <div><strong>CLABE:</strong> 646017230197572477</div>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-medium text-purple-800 mb-2">Contacto</h4>
            <div className="text-sm text-purple-700 space-y-1">
              <div><strong>WhatsApp:</strong> +52 55 1000 0615</div>
              <div><strong>Email:</strong> inversiones@citruspatrimonial.mx</div>
              <div><strong>Horario:</strong> Lun-Vie 9:00-18:00</div>
            </div>
          </div>
          
          <div className="p-4 bg-amber-50 rounded-lg">
            <h4 className="font-medium text-amber-800 mb-2">Importante</h4>
            <p className="text-sm text-amber-700">
              Los pagos deben realizarse puntualmente para mantener el contrato vigente. 
              En caso de retraso, se aplicarán los intereses moratorios especificados en el contrato.
              {isEditingDates && ' Las fechas personalizadas quedarán registradas en tu contrato.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmortizationTab;